import React, { useEffect, useRef, useState } from "react";
import REIToggablePanel from "../../../components/ui/toggablePanel";
import { IGoalPostData, IMyEntityAndGoals } from "../../../interface/myEntityAndGoals";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { formatCurrencyWithDecimal } from "../../../utils/commonUtil";
import { REIButton, REIInputText } from "../../../components/ui";
import { ApiEndPoints } from "../../../utils/endpoints";
import { httpPost } from "../../../services/httpService";
import { Toast } from "primereact/toast";
import { MEMBER_KEY } from "../../../utils/constants";

interface IGoalsInNumber {
  portfolio: string;
  PortfolioValue: number;
  Equity: number;
  AnnualRent: number;
  PreTaxCashflow: number;
  PostTaxCashflow: number;
}

interface IGoalsInNumberProps {
  goalsData: IMyEntityAndGoals;
  viewOption: boolean;
  refreshData: () => void;
}

const GoalsInNumber: React.FC<IGoalsInNumberProps> = ({ goalsData, viewOption, refreshData }) => {
  const [goals, setGoals] = useState<IGoalsInNumber[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editRowData, setEditRowData] = useState<IGoalsInNumber | null>(null);
  const toastRef = useRef<Toast>(null);
  const memberId = localStorage.getItem(MEMBER_KEY);

  useEffect(() => {
    if (goalsData) {
      setGoals(TransformData());
    }
  }, [goalsData, viewOption]);

  const onRowClick = (e) => {
    if (e.index === 0 && !isEditing) {
      const rowData = goals[0];
      setEditRowData({
        ...rowData,
        PortfolioValue: stripDollar(rowData.PortfolioValue),
        Equity: stripDollar(rowData.Equity),
        AnnualRent: stripDollar(rowData.AnnualRent),
        PreTaxCashflow: stripDollar(rowData.PreTaxCashflow),
        PostTaxCashflow: stripDollar(rowData.PostTaxCashflow),
      });
      setIsEditing(true);
    }
  };

  const stripDollar = (value: any) => {
    if (typeof value === "string") {
      return Number(value.replace(/[$,()]/g, "")).toFixed(2);
    }
    return value;
  };

  const handleInputChange = (field, value) => {
    setEditRowData((prev) => ({ ...prev!, [field]: value }));
  };

  const onSave = async () => {
    setIsEditing(false);

    try {
      const result = await httpPost(`${ApiEndPoints.tracker}MemberEntity/Update/`, mapEntityAndGoalsToPostData());
      if (result.status === 200) {
        toastRef.current?.show({
          severity: "success",
          summary: "Success",
          detail: "Goals updated successfully",
          life: 3000,
        });
        refreshData();
      } else {
        throw new Error(result.error);
      }
    } catch (error: any) {
      toastRef.current?.show({
        severity: "error",
        summary: "Error",
        detail: error.message,
        life: 3000,
      });
    }
  };

  const onCancel = () => {
    setIsEditing(false);
    setEditRowData(null);
  };

  const transformKey = (key: string) => {
    switch (key) {
      case "VarianceActualsVsForecast":
        return "$ Var. on forecast";
      case "VarianceActualsVsTarget":
        return "$ Var. on target";
      default:
        return key;
    }
  };

  const currencyTemplate = (value: any) => {
    const numericValue = Number(value);
    return (
      <span className={numericValue < 0 ? "text-red-600" : ""}>
        {numericValue < 0 ? `(${formatCurrencyWithDecimal(numericValue)})` : formatCurrencyWithDecimal(numericValue)}
      </span>
    );
  };

  const TransformData = () => {
    const goals = viewOption ? goalsData.GoalsTotal : goalsData.GoalsPortion;
    const data = Object.keys(goals).map((key) => ({
      portfolio: transformKey(key),
      PortfolioValue: goals[key].PortfolioValue,
      Equity: goals[key].Equity,
      AnnualRent: goals[key].AnnualRent,
      PreTaxCashflow: goals[key].PreTaxCashflow,
      PostTaxCashflow: goals[key].PostTaxCashflow,
    }));
    const thirdElement = data.splice(2, 1)[0];
    data.unshift(thirdElement);
    [data[3], data[4]] = [data[4], data[3]];
    return data;
  };

  const renderInputOrText = (rowData, field: keyof IGoalsInNumber) => {
    const isFirstRow = goals.indexOf(rowData) === 0;
    if (isEditing && isFirstRow && editRowData) {
      return (
        <REIInputText
          id={`meggn-txt-${field.toLowerCase()}`}
          value={editRowData[field]}
          onChange={(e) => handleInputChange(field, e.target.value)}
          type="text"
          name={field}
        />
      );
    }
    return <span>{currencyTemplate(rowData[field])}</span>;
  };

  const actionTemplate = (rowData: IGoalsInNumber) => {
    const index = goals.indexOf(rowData);
    if (index === 0 && isEditing) {
      return (
        <div className="flex gap-2">
          <REIButton id="meggn-btn-save" label="Save" onClick={onSave} classNames="p-button-sm p-button-success" />
          <REIButton
            id="meggn-btn-cancel"
            label="Cancel"
            onClick={onCancel}
            classNames="p-button-sm p-button-secondary"
          />
        </div>
      );
    }
    return null;
  };

  const mapEntityAndGoalsToPostData = (): IGoalPostData => {
    let goalsPostData: IGoalPostData = {} as IGoalPostData;

    goalsPostData.CountryCode = goalsData.CountryCode;
    goalsPostData.EntityName = goalsData.EntityName;
    goalsPostData.EntityTypeCode = goalsData.EntityTypeCode;
    goalsPostData.GoalAfterTaxCashFlow = Number(editRowData?.PostTaxCashflow);
    goalsPostData.GoalAnnualRent = Number(editRowData?.AnnualRent);
    goalsPostData.GoalDesc = goalsData.Goals;
    goalsPostData.GoalEquityUnlocked = Number(editRowData?.Equity);
    goalsPostData.GoalPortfolioValue = Number(editRowData?.PortfolioValue);
    goalsPostData.GoalPreTaxCashFlow = Number(editRowData?.PreTaxCashflow);
    goalsPostData.MemberEntityId = goalsData.EntityId;
    goalsPostData.MemberId = memberId;
    goalsPostData.MemberProfiles = goalsData.MemberProfiles;
    goalsPostData.Note = goalsData.MyNotes || "";
    return goalsPostData;
  };

  return (
    <div>
      <REIToggablePanel
        header={"YOUR GOALS IN NUMBERS – AS OF TODAY"}
        istoggleable={false}
        contentComponent={
          <div className={goalsData.EntityId != "" && goals[0] ? "altRowShadeReverse" : "altRowShade"}>
            <DataTable
              value={goals}
              onRowClick={goalsData.EntityId != "" && onRowClick}
              rowClassName={(rowData) => (rowData === goals[0] && goalsData.EntityId != "" ? "!bg-sky-100" : "")}
            >
              <Column header="" field="portfolio" />
              <Column
                header="Portfolio Value"
                field="PortfolioValue"
                body={(row) => renderInputOrText(row, "PortfolioValue")}
              />
              <Column header="Equity" field="Equity" body={(row) => renderInputOrText(row, "Equity")} />
              <Column header="Annual Rent" field="AnnualRent" body={(row) => renderInputOrText(row, "AnnualRent")} />
              <Column
                header="Pre-tax Cash Flow"
                field="PreTaxCashflow"
                body={(row) => renderInputOrText(row, "PreTaxCashflow")}
              />
              <Column
                header="After-tax Cash Flow"
                field="PostTaxCashflow"
                body={(row) => renderInputOrText(row, "PostTaxCashflow")}
              />
              <Column body={actionTemplate} />
            </DataTable>
          </div>
        }
        defaultCollapse={false}
      ></REIToggablePanel>
    </div>
  );
};

export default GoalsInNumber;
